<template>
  <div>
    <div class="search-header">
      <div class="section is-medium">
        <div class="container">
          <h1 class="title is-h2">{{ node?.title }}</h1>
          <SearchInput v-model="searchTerm" class="mt-3" size="lg" />
        </div>
      </div>
    </div>
    <div class="page">
      <div class="section">
        <div class="container">
          <div class="search-results" :class="{ 'cursor-wait': pending }">
            <h2 v-if="data?.results" class="title is-3">
              {{ $texts('search_results', 'Suchergebnisse') }}
            </h2>
            <ul>
              <li
                v-for="hit in data?.results"
                :key="hit.id"
                class="search-result"
              >
                <VuepalLink :to="hit.url">
                  <h3 class="title is-5 is-blue" v-html="hit.title" />
                  <p class="text mb-2 is-size-6" v-html="hit.text" />
                  <p class="small has-text-grey-light is-size-7">
                    {{ $texts('search_type', 'Typ') }}: {{ hit.type }} -
                    {{ $texts('search_category', 'Kategorie') }}:
                    {{ hit.category }} -
                    {{ hit.date }}
                  </p>
                </VuepalLink>
              </li>
            </ul>
            <pagination
              v-if="data && data?.total > 0"
              :page="page"
              :per-page="limit"
              :total="data?.total"
              :is-sticky="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { NodePageFragment } from '#graphql-operations'

definePageMeta({
  name: 'search',
  languageMapping: {
    de: '/suche',
    fr: '/fr/chercher',
    it: '/it/cerca',
    en: '/en/search',
  },
})

useHead({
  meta: [{ name: 'robots', content: 'noindex' }],
})

const { $texts } = useNuxtApp()
const nuxtRoute = useRoute()
const searchTerm = useQueryString('query')
const language = useCurrentLanguage()
const queryStringPage = useQueryString('page')

const page = computed(() => {
  return queryStringPage.value ? parseInt(queryStringPage.value) : 1
})

const initData = await useInitData()
const domain = initData.value.siteInfo.domain
const limit = ref<number>(12)

const searchQuery = computed(() => {
  return {
    term: searchTerm.value || undefined,
    language: language.value,
    page: page.value - 1,
    size: limit.value,
    domain,
  }
})

const { data, pending } = await useFetch('/api/search', {
  query: searchQuery,
})

// Get the data.
const { data: query } = await useAsyncData(nuxtRoute.path, () => {
  return useGraphqlQuery('route', {
    path: nuxtRoute.path,
  }).then((v) => v.data)
})

// Handles redirects and metatags.
const { entity: node } = await useDrupalRoute<NodePageFragment>(query.value, {
  noError: true,
})
setBreadcrumbLinksFromRoute(query.value)
setLanguageLinksFromRoute(query.value)
await renderPageDependencies()
</script>

<style lang="scss">
.search-result {
  a {
    color: $text;
  }

  .title {
    color: $blue;
  }

  em {
    background: #ffffc3;
    font-style: normal;
    font-weight: bold;
    padding: 0 7px;
  }

  & {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid $yellow;
  }
}
</style>
